import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        toast.success('Subscription activated!');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        // Still processing, poll again
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus(sessionId);
        }, 2000);
      }
    } catch (error) {
      console.error('Payment status error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl p-8 text-center border border-slate-100 shadow-sm">
          {status === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#0a2540] mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                Processing payment...
              </h1>
              <p className="text-slate-600">
                Please wait while we confirm your subscription.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                You're all set!
              </h1>
              <p className="text-slate-600 mb-6">
                Your subscription is active. Start creating unlimited sales assets.
              </p>
              <Button
                onClick={() => navigate('/create')}
                className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-12 px-6"
                data-testid="success-create-btn"
              >
                Create an Asset
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⚠️</span>
              </div>
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                Something went wrong
              </h1>
              <p className="text-slate-600 mb-6">
                We couldn't confirm your payment. Please contact support if you were charged.
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/subscribe')}
                  variant="outline"
                  className="flex-1"
                  data-testid="error-retry-btn"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/library')}
                  className="flex-1 bg-[#0a2540]"
                  data-testid="error-library-btn"
                >
                  Go to Library
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
